/**
 * Monologue bar — bottom-third typewriter caption. Letter-by-letter reveal on
 * `monologue:play`; hidden on `monologue:done` (emitted by AUDIO per
 * CONTRACTS.md). Self-hides after duration + grace if done never arrives.
 * Owned by UI.
 */
import type { EventBus } from '../core/types'
import {
  HIDDEN_CLASS,
  MONOLOGUE_FALLBACK_GRACE_MS,
  TYPE_MAX_CHAR_MS,
  TYPE_MIN_CHAR_MS,
  TYPE_PORTION_OF_BEAT
} from './constants'
import { el } from './dom'

const MIN_CHAR_COUNT = 1

export const createMonologueBar = (root: HTMLElement, bus: EventBus): void => {
  const bar = el('div', `gum-monologue ${HIDDEN_CLASS}`)
  const text = el('p', 'gum-monologue-text')
  bar.append(text)
  root.append(bar)

  let activeBeatId: string | null = null
  let revealIntervalId = 0
  let fallbackTimeoutId = 0

  const stopTimers = (): void => {
    window.clearInterval(revealIntervalId)
    window.clearTimeout(fallbackTimeoutId)
  }

  const hide = (): void => {
    stopTimers()
    activeBeatId = null
    bar.classList.add(HIDDEN_CLASS)
  }

  bus.on('monologue:play', ({ beat }) => {
    stopTimers()
    activeBeatId = beat.id
    bar.classList.remove(HIDDEN_CLASS)
    text.textContent = ''

    const chars = Array.from(beat.text)
    const revealBudgetMs = beat.durationMs * TYPE_PORTION_OF_BEAT
    const perCharMs = Math.min(
      TYPE_MAX_CHAR_MS,
      Math.max(TYPE_MIN_CHAR_MS, revealBudgetMs / Math.max(MIN_CHAR_COUNT, chars.length))
    )

    let shown = 0
    revealIntervalId = window.setInterval(() => {
      shown += 1
      text.textContent = chars.slice(0, shown).join('')
      if (shown >= chars.length) {
        window.clearInterval(revealIntervalId)
      }
    }, perCharMs)

    fallbackTimeoutId = window.setTimeout(hide, beat.durationMs + MONOLOGUE_FALLBACK_GRACE_MS)
  })

  bus.on('monologue:done', ({ beatId }) => {
    if (beatId !== activeBeatId) return
    hide()
  })
}
